import {
  ReactText,
  useMemo,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import getMetrics from '../useMetricMatrix/fetch/getMetrics';
import DashboardGadgetContext from '../../contexts/DashboardGadgetContext';
import ComparisonContext from '../../contexts/ComparisonContext';
import useUsedMetrics from '../useUsedMetrics';
import GqlClientContext from '../../contexts/GqlClientContext';
import useDateScope from '../useDateScope';
import { toInterval } from '../../components/V5Gadget/toAutoInterval';
import moment from 'moment';
import useFilterInput from '../useFilterInput';
import useLockedDebouncedEffect from '../useLockedDebouncedEffect';
import useRegisterInsightsGadgetValues from 'hooks/useRegisterInsightsGadgetValues';
import useToMetricInput from 'hooks/useToMetricInput';
import ChartForecastContext from 'contexts/ChartForecastContext';
import MetricCacheContext from 'contexts/MetricCacheContext';
import metricTypeCheckers from 'types/metricTypeCheckers';
import splitPredictedRows from './splitPredictedRows';
import { useBuildDateScope } from 'hooks/useDateScope';
import { getUsedMetrics } from 'hooks/useUsedMetrics';
import MetricOptionsContext from 'contexts/MetricOptionsContext';
import SavedFiltersContext from 'screens/DataManager/DatasetFilters/SavedFilters/context/SavedFiltersContext';
import { usePlatesToFilterInput } from 'screens/GoalShow/useGoalData';
import { getSeriesDataKey } from 'components/V5Gadget/seriesDataKey';
import groupSeriesByOverrides from './queryOverrides/groupSeriesByOverrides';
import toGroupFilterInput from './queryOverrides/toGroupFilterInput';
import toQueryChartDef from './queryOverrides/toQueryChartDef';
import alignGroupResponses, {
  getAlignKey,
  GroupResponse,
} from './queryOverrides/alignGroupResponses';
import { toSeriesName } from './queryOverrides/describeQueryOverrides';

/**
 * The requests for series with query overrides: one per distinct set of
 * overrides, each with its own filters and date scope
 */
interface OverrideGroupsInput {
  groups: {
    key: string;
    input: Queries.GetMetricsInput;
    isOffset: boolean;
  }[];
}

type OverrideGroupsResponse = { [groupKey: string]: MetricsResponse };

const getOverrideGroupMetrics = async ({
  groups,
}: OverrideGroupsInput): Promise<OverrideGroupsResponse> => {
  const responses = await Promise.all(
    groups.map(({ input }) => getMetrics(input)),
  );
  const result: OverrideGroupsResponse = {};
  groups.forEach(({ key }, index) => {
    result[key] = responses[index];
  });
  return result;
};

const useGetMetricsInputs = (
  chartDef: V5ChartDefinition,
  autoInterval?: AutoInterval,
) => {
  // Context
  const { client } = useContext(GqlClientContext);
  const { filterInput } = useFilterInput({
    field: chartDef.dimensionA ? chartDef.dimensionA.field : undefined,
  });
  const toMetricInput = useToMetricInput();
  const { dashboardGadget } = useContext(DashboardGadgetContext);
  const { currentComparison: comparison } = useContext(ComparisonContext);
  const { isIdResolved } = useContext(MetricCacheContext);
  const forecast = useContext(ChartForecastContext);
  const forecastPeriods = forecast ? forecast.periods : undefined;

  // Hooks
  const currentDateScope = useDateScope({});
  const previousDateScope = useDateScope({
    comparison,
    // An 'auto' trend's comparison is taken as the interval it resolved to
    trendInterval: autoInterval ? autoInterval.interval : undefined,
  });
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const buildDateScope = useBuildDateScope({});
  const platesToFilterInput = usePlatesToFilterInput();
  const { base, groups } = useMemo(
    () => groupSeriesByOverrides(chartDef),
    [chartDef],
  );
  // Without overrides the chart's own query is untouched
  const baseChartDef = useMemo(
    () =>
      groups.length === 0
        ? chartDef
        : toQueryChartDef({ chartDef, series: base }),
    [base, chartDef, groups.length],
  );
  const { directlyUsed } = useUsedMetrics(chartDef);
  const { usedMetrics } = useUsedMetrics(baseChartDef);

  // A compound metric's input metrics load on-demand in a separate cycle from
  // the compound definition itself. If we fire before they resolve, the request
  // carries the full expression but a partial `metrics` list (missing inputs are
  // silently dropped by useUsedMetrics), which the backend rejects as an
  // "Invalid expression". Gate the request until every compound input reaches a
  // terminal state (loaded or errored — isIdResolved covers both, so a genuinely
  // deleted input doesn't block forever).
  const compoundInputsResolved = useMemo(
    () =>
      directlyUsed
        .filter(metricTypeCheckers.isCompoundMetric)
        .flatMap((cm) => cm.metricIds)
        .every((id) => isIdResolved(id)),
    [directlyUsed, isIdResolved],
  );

  const currentInput = useMemo((): Queries.GetMetricsInput | undefined => {
    if (
      base.length === 0 ||
      usedMetrics.length === 0 ||
      !compoundInputsResolved
    ) {
      return undefined;
    }

    return {
      chartDef: baseChartDef,
      filterInput,
      dateScope: currentDateScope,
      toMetricInput,
      usedMetrics,
      isDashboardGadget: !!dashboardGadget,
      autoInterval,
      client,
      // Only the current period is forecast
      forecastPeriods,
    };
  }, [
    autoInterval,
    base.length,
    baseChartDef,
    client,
    compoundInputsResolved,
    currentDateScope,
    dashboardGadget,
    filterInput,
    forecastPeriods,
    toMetricInput,
    usedMetrics,
  ]);

  const previousInput = useMemo(() => {
    if (
      !comparison ||
      base.length === 0 ||
      usedMetrics.length === 0 ||
      !compoundInputsResolved
    ) {
      return undefined;
    }

    return {
      chartDef: baseChartDef,
      filterInput,
      dateScope: previousDateScope,
      toMetricInput,
      usedMetrics,
      isDashboardGadget: !!dashboardGadget,
      autoInterval,
      client,
    };
  }, [
    autoInterval,
    base.length,
    baseChartDef,
    client,
    comparison,
    compoundInputsResolved,
    dashboardGadget,
    filterInput,
    previousDateScope,
    toMetricInput,
    usedMetrics,
  ]);

  // Comparison and forecast belong to the chart's own query, not these
  const groupsInput = useMemo((): OverrideGroupsInput | undefined => {
    if (groups.length === 0 || !compoundInputsResolved) {
      return undefined;
    }

    return {
      groups: groups.map(({ key, overrides, series }) => {
        const { dateOffset, dateField } = overrides;
        const isOffset = !!dateOffset && dateOffset.count > 0;
        const groupChartDef = toQueryChartDef({ chartDef, series });
        return {
          key,
          isOffset,
          input: {
            chartDef: groupChartDef,
            filterInput: toGroupFilterInput({
              filterInput,
              overrides,
              platesToFilterInput,
            }),
            dateScope: buildDateScope({
              comparison: isOffset
                ? { compareType: dateOffset.type }
                : undefined,
              comparisonSteps: isOffset ? dateOffset.count : undefined,
              dateField,
            }),
            toMetricInput,
            usedMetrics: getUsedMetrics({
              series: groupChartDef.series,
              metricOptionsLookup,
            }).usedMetrics,
            isDashboardGadget: !!dashboardGadget,
            autoInterval,
            client,
          },
        };
      }),
    };
  }, [
    autoInterval,
    buildDateScope,
    chartDef,
    client,
    compoundInputsResolved,
    dashboardGadget,
    filterInput,
    groups,
    metricOptionsLookup,
    platesToFilterInput,
    toMetricInput,
  ]);

  return {
    currentInput,
    previousInput,
    groupsInput,
    currentDateScope,
    base,
    groups,
  };
};

const useMetricResponses = (
  chartDef: V5ChartDefinition,
  autoInterval?: AutoInterval,
) => {
  // Context
  const { currentComparison: comparison } = useContext(ComparisonContext);
  const forecast = useContext(ChartForecastContext);

  // State
  const [currentResponse, setCurrentResponse] = useState<
    | {
        input: Queries.GetMetricsInput;
        response: MetricsResponse;
      }
    | undefined
  >();
  const [previousResponse, setPreviousResponse] = useState<
    | {
        input: Queries.GetMetricsInput;
        response: MetricsResponse;
      }
    | undefined
  >();
  const [groupsResponse, setGroupsResponse] = useState<
    | {
        input: OverrideGroupsInput;
        response: OverrideGroupsResponse;
      }
    | undefined
  >();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Hooks
  const {
    currentInput,
    previousInput,
    groupsInput,
    currentDateScope,
    base,
    groups,
  } = useGetMetricsInputs(chartDef, autoInterval);
  const hasBase = base.length > 0;

  const handleCurrentResponse = useCallback(
    (response: MetricsResponse) => {
      if (!currentInput) {
        return;
      }
      // Predicted buckets aren't data the chart draws
      const { actual, predicted } = splitPredictedRows(response);
      setCurrentResponse({
        input: currentInput,
        response: actual,
      });
      if (forecast) {
        forecast.onPredictedRows(predicted);
      }
    },
    [currentInput, forecast],
  );

  const handlePreviousResponse = useCallback(
    (response: MetricsResponse) => {
      if (!previousInput) {
        return;
      }
      setPreviousResponse({
        input: previousInput,
        response,
      });
    },
    [previousInput],
  );

  const handleGroupsResponse = useCallback(
    (response: OverrideGroupsResponse) => {
      if (!groupsInput) {
        return;
      }
      setGroupsResponse({ input: groupsInput, response });
    },
    [groupsInput],
  );

  // Effects
  useLockedDebouncedEffect({
    args: currentInput,
    callback: getMetrics,
    responseHandler: handleCurrentResponse,
  });
  useLockedDebouncedEffect({
    args: previousInput,
    callback: getMetrics,
    responseHandler: handlePreviousResponse,
  });
  useLockedDebouncedEffect({
    args: groupsInput,
    callback: getOverrideGroupMetrics,
    responseHandler: handleGroupsResponse,
  });

  useEffect(() => {
    setIsLoading(true);
  }, [currentInput, previousInput, groupsInput]);

  const isGroupsLoaded =
    groups.length === 0 ||
    (!!groupsInput && !!groupsResponse && groupsResponse.input === groupsInput);
  useEffect(() => {
    const isBaseLoaded =
      !hasBase || (!!currentResponse && (!comparison || !!previousResponse));
    setIsLoading(!isBaseLoaded || !isGroupsLoaded);
  }, [comparison, currentResponse, hasBase, isGroupsLoaded, previousResponse]);

  return {
    currentResponse: hasBase ? currentResponse : undefined,
    previousResponse: hasBase ? previousResponse : undefined,
    groupsResponse:
      groups.length > 0 && isGroupsLoaded && groupsResponse
        ? groupsResponse.response
        : undefined,
    currentDateScope,
    groups,
    isLoadingResponses: isLoading,
  };
};

const useV5ChartData = (
  chartDef: V5ChartDefinition,
  autoInterval?: AutoInterval,
) => {
  // Context
  const { currentComparison: comparison } = useContext(ComparisonContext);

  // State
  const [data, setData] = useState<V5ChartData>();
  const [comparisonData, setComparisonData] = useState<V5ChartData>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Hooks
  const {
    currentResponse,
    previousResponse,
    groupsResponse,
    currentDateScope,
    groups,
    isLoadingResponses,
  } = useMetricResponses(chartDef, autoInterval);
  const { directlyUsed } = useUsedMetrics(chartDef);
  const { savedFilters } = useContext(SavedFiltersContext);

  /**
   * The rows each series draws. Series with query overrides were fetched
   * separately, so every response is lined up onto one axis first.
   */
  const seriesRows = useMemo(():
    | {
        base: MetricsResponse | undefined;
        byGroup: { [groupKey: string]: MetricsResponse };
      }
    | undefined => {
    if (groups.length === 0) {
      return currentResponse
        ? { base: currentResponse.response, byGroup: {} }
        : undefined;
    }
    if (!groupsResponse) {
      return undefined;
    }

    const hasBase =
      chartDef.series.length > groups.flatMap((g) => g.series).length;
    if (hasBase && !currentResponse) {
      return undefined;
    }
    const responses: GroupResponse[] = [
      ...(hasBase && currentResponse
        ? [
            {
              rows: currentResponse.response,
              dateScope: currentResponse.input.dateScope,
              isOffset: false,
            },
          ]
        : []),
      ...groups.map(({ key, overrides }): GroupResponse => {
        const rows = groupsResponse[key] || [];
        const isOffset =
          !!overrides.dateOffset && overrides.dateOffset.count > 0;
        // Rows carry the date scope they were asked for
        const askedFor =
          rows.length > 0 ? (rows[0].dateScope as DateRangeInput) : undefined;
        return {
          rows,
          isOffset,
          dateScope: isOffset && askedFor ? askedFor : currentDateScope,
        };
      }),
    ];

    const aligned = alignGroupResponses({
      chartDef,
      autoInterval,
      currentDateScope,
      responses,
    });
    const byGroup: { [groupKey: string]: MetricsResponse } = {};
    groups.forEach((g, i) => {
      byGroup[g.key] = aligned[i + (hasBase ? 1 : 0)];
    });
    return { base: hasBase ? aligned[0] : undefined, byGroup };
  }, [
    autoInterval,
    chartDef,
    currentDateScope,
    currentResponse,
    groups,
    groupsResponse,
  ]);

  // Effects
  // set the current period data
  useEffect(() => {
    if (!seriesRows) {
      return;
    }

    const { series } = chartDef;
    const result = {} as V5ChartData;
    series.forEach((s) => {
      const { metricId } = s;
      const metric = directlyUsed.find((o) => o.id === metricId);
      const group = groups.find((g) => g.series.includes(s));
      const rows = group ? seriesRows.byGroup[group.key] : seriesRows.base;
      if (!metric) {
        console.warn(`Metric ${metricId} not found`);
      } else if (rows) {
        result[getSeriesDataKey(s)] = {
          name: toSeriesName({
            metricName: metric.name,
            series: s,
            savedFilters,
          }),
          metric,
          response: rows,
        };
      }
    });
    setData(result);
  }, [chartDef, directlyUsed, groups, savedFilters, seriesRows]);

  // set the comparison period data
  useEffect(() => {
    if (!previousResponse || !comparison || !currentResponse || !seriesRows) {
      return;
    }

    // Comparisons are of the chart's own query, so not of series with
    // query overrides
    const series = chartDef.series.filter(
      (s) => !groups.some((g) => g.series.includes(s)),
    );
    const currentRows = seriesRows.base || [];

    /**
     * It's possible that the comparison covers a date range
     * which we don't have data against.
     *
     * In this situation, we want to fluff out the start of the
     * comparison period so that we can align the data as expected
     *
     * See: https://fleetops.atlassian.net/browse/FWA-3297
     */
    const responseWithMissingDates = (() => {
      let newResponse = [...previousResponse.response];
      if (newResponse.length === 0) {
        return newResponse;
      }
      const interval = toInterval(newResponse[0].interval);
      if (!interval) {
        return newResponse;
      }
      if (
        comparison.compareType === 'lastYear' &&
        newResponse.length >= 2 &&
        currentRows.length >= 2
      ) {
        const lastItemInPrev = newResponse[newResponse.length - 1].date;
        const lastItemInCurrent = currentRows[currentRows.length - 1].date;

        if (
          moment(lastItemInCurrent).get(interval) !==
          moment(lastItemInPrev).get(interval)
        ) {
          newResponse.pop();
        }
      }
      const expectedNumDates = currentRows.length;
      if (newResponse.length < expectedNumDates) {
        const missingNumDates = expectedNumDates - newResponse.length;

        for (let i = missingNumDates; i > 0; i--) {
          const firstDate = newResponse[0].date;
          const date = moment(firstDate)
            .subtract({ [interval]: 1 })
            .format('YYYY-MM-DD');

          // Do not inject in dates outside of the requested range
          if (
            previousResponse.input.dateScope.startDate &&
            previousResponse.input.dateScope.endDate &&
            date >= previousResponse.input.dateScope.startDate &&
            date <= previousResponse.input.dateScope.endDate
          ) {
            const newItem = {
              date,
            };
            series.forEach((s) => {
              // @ts-ignore
              newItem[s.metricId] = undefined;
            });
            newResponse = [newItem, ...newResponse];
          }
        }
      }
      return newResponse;
    })();

    // Series with query overrides can add terms to the axis: line the
    // comparison up with the terms the chart draws
    const alignKey = getAlignKey(chartDef);
    const comparisonRows =
      groups.length > 0 && alignKey && alignKey !== 'date'
        ? currentRows.map(
            (row) =>
              responseWithMissingDates.find(
                (p) => String(p[alignKey]) === String(row[alignKey]),
              ) || { [alignKey]: row[alignKey] },
          )
        : responseWithMissingDates;

    const result = {} as V5ChartData;
    series.forEach((s) => {
      const { metricId } = s;
      const metric = directlyUsed.find((o) => o.id === metricId);
      if (!metric) {
        console.warn(`Metric ${metricId} not found`);
      } else {
        result[metricId] = {
          name: s.displayName ? s.displayName : metric.name,
          metric,
          response: comparisonRows as {
            [key: string]: ReactText;
          }[],
        };
      }
    });

    setComparisonData(result);
  }, [
    chartDef,
    comparison,
    currentResponse,
    previousResponse,
    directlyUsed,
    groups,
    seriesRows,
  ]);

  useEffect(() => {
    if (comparison) {
      setIsLoading(isLoadingResponses || !data || !comparisonData);
    } else {
      setIsLoading(isLoadingResponses || !data);
    }
  }, [comparison, comparisonData, data, isLoadingResponses]);

  // Publish the rendered figures to the AI Insights Chat when this gadget is on a
  // report canvas. A lazy getter — nothing is computed unless a question is sent —
  // and a no-op wherever there's no Insights view scope (Explore modal, gadget
  // previews, Storybook). Covers the chart-shaped gadgets; the table- and
  // stack-shaped ones are captured in useMetricMatrix instead.
  useRegisterInsightsGadgetValues({ chartDef, data, isLoading });

  return { data, isLoading, comparisonData };
};

export default useV5ChartData;
